import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useProductData } from "../context/ProductDataContext";
import { FUNCTION_LIST, fmtFt } from "../lib/constants";
import { getRequiresHubs, getCompatibleHubs, hasDirectControl } from "../lib/specsUtils";
import EcosystemStep from "../components/smart_home_builder/EcosystemStep";
import FunctionSelector from "../components/smart_home_builder/FunctionSelector";
import PreferencesStep from "../components/smart_home_builder/PreferencesStep";
import ProductPicker from "../components/smart_home_builder/ProductPicker";
import HubRecommendations from "../components/smart_home_builder/HubRecommendations";
import PlanSummary from "../components/smart_home_builder/PlanSummary";
import { canon } from "../lib/seo";

export default function SmartHomeBuilderPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    products,
    categories,
    loading,
    byId,
    productsByFunctionId,
    productsByCategoryId,
  } = useProductData();

  const [step, setStep] = useState(1);

  // Ökoszisztéma (persist)
  const [ecosystem, setEcosystem] = useState(() => sessionStorage.getItem("shb:ecosystem") || "");
  useEffect(() => {
    if (ecosystem && ecosystem.trim() !== "") {
      sessionStorage.setItem("shb:ecosystem", ecosystem);
    } else {
      sessionStorage.removeItem("shb:ecosystem");
    }
  }, [ecosystem]);

  // Funkciók + választások
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [selectedProductsByFunc, setSelectedProductsByFunc] = useState({});
  const [selectedHubIds, setSelectedHubIds] = useState(new Set());

  // Preferenciák (persist)
  const [prefs, setPrefs] = useState(() => {
    const raw = sessionStorage.getItem("shb:prefs");
    return raw
      ? JSON.parse(raw)
      : {
        hasHub: "no",
        existingHubId: "",
        onlyCompatibleWithExisting: false,
        hubPolicy: "hub-ok",
      };
  });
  useEffect(() => {
    sessionStorage.setItem("shb:prefs", JSON.stringify(prefs || {}));
  }, [prefs]);

  const orderedFunctions = useMemo(
    () => FUNCTION_LIST.filter((f) => selectedFunctions.includes(f.id)),
    [selectedFunctions]
  );

  const isFunctionStep = step >= 4 && step <= 3 + orderedFunctions.length;
  const currentFunctionId = isFunctionStep ? orderedFunctions[step - 4]?.id : null;

  // Funkció termékek – index + PREF SZŰRÉS
  const functionProducts = useMemo(() => {
    if (!currentFunctionId) return [];
    let list = productsByFunctionId.get(currentFunctionId) || [];

    if (prefs.hubPolicy === "no-hub-only") {
      list = list.filter((p) => hasDirectControl(p));
    }

    if (prefs.hasHub === "yes" && prefs.existingHubId && prefs.onlyCompatibleWithExisting) {
      const hubId = prefs.existingHubId;
      list = list.filter((p) => {
        const req = getRequiresHubs(p);
        const comp = getCompatibleHubs(p);
        if (hasDirectControl(p)) return true;
        return (Array.isArray(req) && req.includes(hubId)) || (Array.isArray(comp) && comp.includes(hubId));
      });
    }

    return list;
  }, [currentFunctionId, prefs, productsByFunctionId]);

  // Összes kiválasztott termék (összes funkcióból)
  const allSelectedProductIds = useMemo(() => {
    const ids = new Set();
    Object.values(selectedProductsByFunc).forEach((byPid) => {
      Object.entries(byPid || {}).forEach(([pid, qty]) => {
        if (qty > 0) ids.add(pid);
      });
    });
    return Array.from(ids);
  }, [selectedProductsByFunc]);

  const allSelectedProducts = useMemo(
    () => allSelectedProductIds.map((id) => byId.get(id)).filter(Boolean),
    [allSelectedProductIds, byId]
  );

  // HUB-ok
  const hubs = useMemo(() => {
    const fromIndex = productsByCategoryId.get("hub");
    if (Array.isArray(fromIndex)) return fromIndex;
    return products.filter((p) => p.categoryId === "hub");
  }, [productsByCategoryId, products]);

  // Kötelező hubot igénylő termékek
  const mandatoryProducts = useMemo(
    () => allSelectedProducts.filter((p) => getRequiresHubs(p).length > 0),
    [allSelectedProducts]
  );

  // 1 hub jelöltek (metszet / kompat)
  const singleHubCandidates = useMemo(() => {
    if (mandatoryProducts.length === 0) {
      const compatSet = new Set();
      allSelectedProducts.forEach((p) => {
        getCompatibleHubs(p).forEach((id) => compatSet.add(id));
      });
      return hubs.filter((h) => compatSet.has(h.id));
    }
    let intersect = new Set(getRequiresHubs(mandatoryProducts[0]));
    for (let i = 1; i < mandatoryProducts.length; i++) {
      const s = new Set(getRequiresHubs(mandatoryProducts[i]));
      intersect = new Set([...intersect].filter((x) => s.has(x)));
      if (intersect.size === 0) break;
    }
    const setIds = new Set(intersect);
    return hubs.filter((h) => setIds.has(h.id));
  }, [mandatoryProducts, hubs, allSelectedProducts]);

  // 2 hub kombók
  const twoHubCombos = useMemo(() => {
    if (mandatoryProducts.length === 0) return [];
    if (singleHubCandidates.length > 0) return [];
    const combos = [];
    for (let i = 0; i < hubs.length; i++) {
      for (let j = i + 1; j < hubs.length; j++) {
        const h1 = hubs[i], h2 = hubs[j];
        const okForAll = mandatoryProducts.every((p) => {
          const req = getRequiresHubs(p);
          return req.includes(h1.id) || req.includes(h2.id);
        });
        if (okForAll) {
          combos.push({ hubs: [h1, h2], approxPrice: (h1.price || 0) + (h2.price || 0) });
        }
      }
    }
    return combos.sort((a, b) => (a.approxPrice || 0) - (b.approxPrice || 0));
  }, [mandatoryProducts, hubs, singleHubCandidates]);

  // Soft ranking
  const softSingleHubRanking = useMemo(() => {
    if (mandatoryProducts.length > 0 || hubs.length === 0) return [];
    const rows = hubs.map((h) => {
      const covered = allSelectedProducts.filter((p) => getCompatibleHubs(p).includes(h.id)).length;
      let score = covered * 10;
      if (typeof h.price === "number") {
        if (h.price < 20000) score += 5;
        else if (h.price < 40000) score += 2;
      }
      const eco = (h.compat && h.compat.ecosystems) || {};
      if (ecosystem === "apple" && eco.homekit) score += 6;
      if (ecosystem === "google" && eco.google) score += 6;
      if (ecosystem === "alexa" && eco.alexa) score += 6;
      return { hub: h, covered, score };
    });
    return rows.filter((r) => r.covered > 0).sort((a, b) => b.score - a.score);
  }, [mandatoryProducts, hubs, allSelectedProducts, ecosystem]);

  const toggleFunction = (id) => {
    setSelectedFunctions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setQtyForCurrentFunction = (pid, newQty) => {
    if (!currentFunctionId) return;
    setSelectedProductsByFunc((prev) => {
      const prevMap = prev[currentFunctionId] || {};
      const nextQty = Math.max(0, Math.min(99, Number(newQty) || 0));
      const nextMap = { ...prevMap };
      if (nextQty === 0) delete nextMap[pid];
      else nextMap[pid] = nextQty;
      return { ...prev, [currentFunctionId]: nextMap };
    });
  };

  const onToggleHub = (hubId, opts = {}) => {
    setSelectedHubIds((prev) => {
      const next = new Set(prev);
      if (opts.force === "add") next.add(hubId);
      else if (opts.force === "remove") next.delete(hubId);
      else next.has(hubId) ? next.delete(hubId) : next.add(hubId);
      return next;
    });
  };

  useEffect(() => {
    if (prefs.hasHub === "yes" && prefs.existingHubId) {
      setSelectedHubIds((prev) => new Set(prev).add(prefs.existingHubId));
    }
  }, [prefs.hasHub, prefs.existingHubId]);

  const proceedToSummary = (idsFromChild) => {
    if (Array.isArray(idsFromChild)) {
      setSelectedHubIds(new Set(idsFromChild));
    }
    navigate("/builder/osszegzes", {
      state: {
        selectedProductsByFunc,
        selectedHubIds: Array.isArray(idsFromChild) ? idsFromChild : Array.from(selectedHubIds),
        ecosystem,
        prefs,
      },
    });
  };

  const resetBuilder = () => {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("shb:filters:")) {
        sessionStorage.removeItem(k);
      }
    }
    sessionStorage.removeItem("shb:selectedHubIds");
    sessionStorage.removeItem("shb:selectedProductsByFunc");
    sessionStorage.removeItem("shb:quantities");
    sessionStorage.removeItem("shb:prefs");
    sessionStorage.removeItem("shb:ecosystem");

    setEcosystem("");
    setSelectedFunctions([]);
    setSelectedProductsByFunc({});
    setSelectedHubIds(new Set());
    setPrefs({
      hasHub: "no",
      existingHubId: "",
      onlyCompatibleWithExisting: false,
      hubPolicy: "hub-ok",
    });
    setStep(1);
    navigate("/builder", { replace: true });
  };

  // === Sticky lábléc logika az egységes navigációhoz (csak a „termékek” és „HUB ajánlás” lépésekben) ===
  const showStickyNav =
    isFunctionStep || step === 4 + orderedFunctions.length;

  const canGoPrev = step > 1;
  const canGoNext =
    (isFunctionStep && !!currentFunctionId) ||
    step === 4 + orderedFunctions.length; // summary + hub ajánló együtt

  const goPrev = () => {
    if (isFunctionStep) setStep((s) => Math.max(1, s - 1));
    else if (step === 4 + orderedFunctions.length) setStep((s) => Math.max(1, s - 1));
  };

  const goNext = () => {
    if (isFunctionStep) setStep((s) => s + 1);
    else if (step === 4 + orderedFunctions.length) {
      // innen „Tovább” → összegzésre navigálás (HUB ajánlóból)
      proceedToSummary();
    }
  };

  if (loading) return <div className="p-6">Betöltés…</div>;

  return (
    <>
      {/* SEO – Smart Home Builder */}
      <title>Okosotthon építő – Solarya</title>
      <meta
        name="description"
        content="Tervezd meg lépésről lépésre az okosotthonod: ökoszisztéma választás, funkciók, preferenciák, kompatibilis termékek és HUB ajánlás."
      />
      <link rel="canonical" href={canon(pathname)} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="Okosotthon építő – Solarya" />
      <meta
        property="og:description"
        content="Ökoszisztéma, funkciók, preferenciák, kompatibilis termékek és HUB ajánlás."
      />
      <meta property="og:url" content={canon(pathname)} />

      {/* =================== HERO (HomePage-stílus, teljes szélességben) =================== */}
      <section className="relative overflow-hidden mb-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            Okosotthon építő
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-3xl">
            Tervezd meg saját intelligens otthonodat lépésről lépésre.{" "}
            Válassz ökoszisztémát, funkciókat, preferenciákat, majd válogass a
            kompatibilis termékek közül – a végén személyre szabott tervet és
            központ-ajánlást kapsz.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => window.scrollTo({ top: 600, behavior: "smooth" })}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
            >
              Építés indítása
            </button>
            <button
              onClick={() => window.scrollTo({ top: 1000, behavior: "smooth" })}
              className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
            >
              Tudj meg többet
            </button>
          </div>
        </div>
      </section>

      {/* =================== OLDAL TARTALOM =================== */}
      <motion.div
        className="p-6 max-w-6xl mx-auto text-[#0c1a26]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">Okosotthon építő</h1>
          <p className="text-gray-700 mt-1">
            Válassz ökoszisztémát, jelöld a funkciókat és preferenciákat, majd állítsd össze a termékeket. A végén tervet és hub-ajánlást kapsz.
          </p>
        </header>

        {/* Lépésjelző + Reset */}
        <div className="flex items-center gap-3 mb-6 text-sm flex-wrap">
          <span className={`px-2 py-1 rounded ${step === 1 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>1. Ökoszisztéma</span>
          <span>→</span>
          <span className={`px-2 py-1 rounded ${step === 2 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>2. Funkciók</span>
          <span>→</span>
          <span className={`px-2 py-1 rounded ${step === 3 ? "bg-blue-600 text-white" : "bg-gray-100"}`}>3. Preferenciák</span>
          {orderedFunctions.map((f, i) => (
            <React.Fragment key={f.id}>
              <span>→</span>
              <span className={`px-2 py-1 rounded ${step === 4 + i ? "bg-blue-600 text-white" : "bg-gray-100"}`}>
                {4 + i}. {f.title} termékek
              </span>
            </React.Fragment>
          ))}
          <span>→</span>
          <span className={`px-2 py-1 rounded ${step === (4 + orderedFunctions.length) ? "bg-blue-600 text-white" : "bg-gray-100"}`}>
            {4 + orderedFunctions.length}. Terv & HUB ajánlás
          </span>

          <span className="grow" />
          <button
            type="button"
            onClick={resetBuilder}
            className="px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            Mindent törlök és újrakezdem
          </button>
        </div>

        {step === 1 && (
          <EcosystemStep
            value={ecosystem}
            onChange={setEcosystem}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <FunctionSelector
            functionList={FUNCTION_LIST}
            selectedFunctions={selectedFunctions}
            onToggle={toggleFunction}
            onNext={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <PreferencesStep
            hubs={hubs}
            prefs={prefs}
            setPrefs={setPrefs}
            onPrev={() => setStep(2)}
            onNext={() => setStep(orderedFunctions.length ? 4 : 4 + orderedFunctions.length)}
          />
        )}

        {isFunctionStep && (
          <ProductPicker
            title={FUNCTION_LIST.find((f) => f.id === currentFunctionId)?.title}
            products={functionProducts}
            quantitiesById={selectedProductsByFunc[currentFunctionId] || {}}
            onChangeQty={setQtyForCurrentFunction}
            onPrev={() => setStep((s) => Math.max(1, s - 1))}
            onNext={() => setStep((s) => s + 1)}
            fmtFt={fmtFt}
            functionId={currentFunctionId}
            ecosystem={ecosystem}
            prefs={prefs}
            hubs={hubs}
            showNav={false} // NAV itt rejtve, a sticky lábléc kezeli
          />
        )}

        {step === 4 + orderedFunctions.length && (
          <div className="space-y-6">
            <PlanSummary
              ecosystem={ecosystem}
              hubs={hubs}
              selectedHubIds={Array.from(selectedHubIds)}
              selectedProducts={allSelectedProducts}
            />
            <HubRecommendations
              allSelectedProducts={allSelectedProducts}
              singleHubCandidates={singleHubCandidates}
              twoHubCombos={twoHubCombos}
              softSingleHubRanking={softSingleHubRanking}
              selectedHubIds={selectedHubIds}
              onToggleHub={onToggleHub}
              fmtFt={fmtFt}
              onPrev={() => setStep((s) => Math.max(1, s - 1))}
              onProceed={proceedToSummary}
              showNav={false} // NAV itt is rejtve, sticky intézi
            />
          </div>
        )}

        {/* Sticky lábléc – csak a termékek és HUB ajánlás lépéseknél */}
        {showStickyNav && (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-t">
            <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
              <button
                className="border px-5 py-2 rounded hover:bg-gray-50"
                onClick={goPrev}
                disabled={!canGoPrev}
              >
                Vissza
              </button>
              <button
                className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={goNext}
                disabled={!canGoNext}
              >
                Tovább
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
}
